// Arquivo sem imports de runtime, de propósito (mesmo padrão de
// src/lib/derivarEstadoNotificacaoD17.ts): fica testável com `node --test` puro,
// sem puxar o client do Supabase (que depende de import.meta.env, exclusivo do
// Vite). `import type` é apagado por inteiro na transpilação — nunca vira um
// import de verdade em tempo de execução.
import type {
    CobrancaAcaoManual, DemandaEsteira, EsteiraFila, EsteiraGrupo,
} from '../../../types';

const DIA_MS = 86_400_000;

/**
 * Filas da régua e filas de documento. O GRUPO é como a tela organiza os cards
 * e mudou em 09/10/2026 (#6209: D+17 e mora foram para "Notificações
 * extrajudiciais"); as AÇÕES do card dependem da origem do trabalho, não do
 * grupo — por isso a tela pergunta por aqui, e não por `d.grupo`.
 */
const FILAS_DA_REGUA: EsteiraFila[] = ['regua_d4', 'regua_d10', 'regua_d17', 'notificacao_extrajudicial', 'regua_d24'];
const FILAS_DE_DOCUMENTO: EsteiraFila[] = [
    'mora_sem_assinatura', 'mora_sem_envio', 'mora_prazo_vencido',
    'advertencia_sem_assinatura', 'advertencia_sem_envio', 'advertencia_prazo_vencido',
    'distrato_em_aberto', 'distrato_aguardando_vigor', 'distrato_aguardando_cliente', 'distrato_assinado_sem_envio',
];
export const ehDaRegua = (d: Pick<DemandaEsteira, 'fila'>): boolean => FILAS_DA_REGUA.includes(d.fila);
export const ehDeDocumento = (d: Pick<DemandaEsteira, 'fila'>): boolean => FILAS_DE_DOCUMENTO.includes(d.fila);

export const GRUPO_DA_FILA: Record<EsteiraFila, EsteiraGrupo> = {
    regua_d4: 'regua',
    regua_d10: 'regua',
    // Notificação extrajudicial num grupo só, do D+17 ao fim do prazo (#6209).
    regua_d17: 'notificacoes',
    notificacao_extrajudicial: 'notificacoes',
    regua_d24: 'regua',
    mora_sem_assinatura: 'notificacoes',
    mora_sem_envio: 'notificacoes',
    mora_prazo_vencido: 'notificacoes',
    advertencia_sem_assinatura: 'documentos',
    advertencia_sem_envio: 'documentos',
    advertencia_prazo_vencido: 'documentos',
    distrato_em_aberto: 'documentos',
    distrato_aguardando_vigor: 'documentos',
    distrato_aguardando_cliente: 'documentos',
    distrato_assinado_sem_envio: 'documentos',
    negativacao_pendente: 'negativacao',
    negativacao_retirar: 'negativacao',
    negativado_spc: 'negativacao',
    followup_vencido: 'negociacao',
    sem_retorno: 'negociacao',
    sem_contato: 'negociacao',
    sem_responsavel: 'negociacao',
};

/**
 * Datas do banco chegam em dois formatos: 'YYYY-MM-DD' (colunas `date`) e ISO
 * completo (`timestamptz`). Ancorar o dia ao meio-dia evita que o fuso de
 * Brasília jogue a data um dia para trás — mesmo cuidado de etapas.ts.
 */
function aoMeioDia(iso: string): Date | null {
    const d = iso.length <= 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(12, 0, 0, 0);
    return d;
}

/** Dias corridos entre `iso` e `agora`. Negativo quando `iso` está no futuro. */
export function diasDesdeISO(iso: string | null | undefined, agora: Date): number {
    if (!iso) return 0;
    const base = aoMeioDia(iso);
    if (!base) return 0;
    const ref = new Date(agora.getTime());
    ref.setHours(12, 0, 0, 0);
    return Math.round((ref.getTime() - base.getTime()) / DIA_MS);
}

/** Dias corridos de `b` até `a` (positivo quando `a` é depois), no fuso local. */
function diasEntre(a: string, b: string): number {
    const x = aoMeioDia(a);
    const y = aoMeioDia(b);
    if (!x || !y) return 0;
    return Math.round((x.getTime() - y.getTime()) / DIA_MS);
}

/** `true` quando a data já passou (o próprio dia do prazo ainda vale). */
export function prazoVencido(iso: string | null | undefined, agora: Date): boolean {
    if (!iso) return false;
    return diasDesdeISO(iso, agora) > 0;
}

/**
 * Piso de sanidade para data digitada por gente.
 *
 * Um campo de data com o ano errado ("0001-01-01", de quem digitou só "1" no
 * ano) vira uma idade de ~740 mil dias. Como a fila ordena por tempo parado, a
 * linha com o pior dado do banco sobe para o topo e é a primeira coisa que todo
 * mundo vê, todo dia. Em 11/09/2026 havia exatamente 1 caso assim em
 * `cobranca_clientes.proximo_contato_em`, e ele encabeçava as 1.117 demandas.
 *
 * Antes de 2020 não há operação da Vigor neste sistema — data assim é erro de
 * digitação, não histórico.
 */
export const DATA_MINIMA_PLAUSIVEL = '2020-01-01';

export function dataPlausivel(iso: string | null | undefined): boolean {
    if (!iso) return false;
    return iso.slice(0, 10) >= DATA_MINIMA_PLAUSIVEL;
}

/**
 * Converte valor monetário para número, aguentando o que o banco realmente tem.
 *
 * ⚠️ `notificacao_mora_logs.valor_total` NÃO é numeric — é TEXT, e guarda quatro
 * formatos diferentes (levantamento de 11/09/2026, 126 linhas):
 *
 *     64x "1042,31"     pt-BR sem milhar
 *     50x "1.042,31"    pt-BR com milhar
 *      3x "1042.31"     já em formato numérico
 *      1x " 4.384,41"   com espaço à esquerda
 *
 * `Number("1.042,31")` é NaN, e um único NaN contamina a soma do KPI inteiro e
 * quebra o comparador da ordenação. Daí este parser existir em vez de um cast.
 *
 * Regra: se há vírgula, ela é o decimal e o ponto é separador de milhar. Sem
 * vírgula, o ponto é o decimal — que é como um campo numeric chegaria. Isso
 * deixa "1.042" (sem centavos) ambíguo; lê como 1.042, não 1042. Nenhuma linha
 * em produção tem esse formato hoje.
 */
export function parseValorBR(bruto: unknown): number | null {
    if (bruto == null) return null;
    if (typeof bruto === 'number') return Number.isFinite(bruto) ? bruto : null;

    const texto = String(bruto).trim();
    if (!texto) return null;

    const normalizado = texto.includes(',')
        ? texto.replace(/\./g, '').replace(',', '.')
        : texto;

    const n = Number(normalizado);
    return Number.isFinite(n) ? n : null;
}

/**
 * Colunas que a esteira lê de um log de documento assinável. Declarado por
 * extensão (e não importado de types.ts) porque a esteira usa um punhado de
 * campos e nem toda tabela de origem tem todos eles — ver `LogDistrato`.
 */
export interface LogDocumento {
    id: number | string;
    uc_numero?: string | null;
    cliente_nome?: string | null;
    cpf_cnpj_cliente?: string | null;
    url_pdf?: string | null;
    /** TEXT no banco, em formato pt-BR — ver `parseValorBR`. Nunca use direto. */
    valor_total?: number | string | null;
    created_at: string;
    user_id?: string | null;
    assinado_em?: string | null;
    enviado_cliente_em?: string | null;
    nao_enviado_cliente_em?: string | null;
    prazo_final?: string | null;
    resultado_verificacao?: string | null;
    cancelado_em?: string | null;
    tipo_cliente?: string | null;
    whatsapp_cliente?: string | null;
    email_cliente?: string | null;
    lembrete_enviado_em?: string | null;
    /** Só a notificação de mora tem: as faturas que ela cobra. */
    fatura_ids?: Array<number | string> | null;
}

/** Só entra no contexto o que tem valor — evita chip vazio no card. */
function contextoDe(pares: Array<[string, unknown]>): Array<{ rotulo: string; valor: string }> {
    return pares
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([rotulo, v]) => ({ rotulo, valor: String(v).trim() }));
}

/** 'YYYY-MM-DD' ou ISO → 'DD/MM/AAAA'. Sem dependência de runtime. */
function diaBR(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const [a, m, d] = iso.slice(0, 10).split('-');
    return a && m && d ? `${d}/${m}/${a}` : null;
}

/**
 * Estado de uma notificação de mora ou de um comunicado de advertência.
 *
 * A ordem importa: um documento assinado mas não enviado não está "aguardando
 * assinatura", e um documento cujo prazo venceu já passou pelo envio. Só o
 * primeiro estado que bate vira demanda — senão o mesmo documento apareceria em
 * três filas ao mesmo tempo.
 *
 * Documento cancelado sai da esteira. Documento em dia (enviado, dentro do
 * prazo, ou já com desfecho registrado) também: não há nada a fazer com ele.
 */
export function normalizarDocumento(
    log: LogDocumento,
    tipo: 'mora' | 'advertencia',
    agora: Date,
): DemandaEsteira | null {
    if (log.cancelado_em) return null;

    const origem = tipo === 'mora' ? 'notificacao_mora_logs' : 'comunicado_advertencia_logs';
    const rotulo = tipo === 'mora' ? 'Notificação extrajudicial' : 'Comunicado de advertência';

    const base = {
        origem: origem as DemandaEsteira['origem'],
        origem_id: String(log.id),
        id: `${origem}:${log.id}`,
        cliente_nome: log.cliente_nome ?? null,
        cpf_cnpj_cliente: log.cpf_cnpj_cliente ?? null,
        uc_numero: log.uc_numero ?? null,
        valor: parseValorBR(log.valor_total),
        criado_em: log.created_at,
        responsavel_id: log.user_id ?? null,
        status: 'pendente' as const,
        url_documento: log.url_pdf ?? null,
        // O contato estava gravado e não aparecia: quem ia cobrar a assinatura
        // ou enviar o documento tinha de sair da tela para achar o número.
        contato_whatsapp: log.whatsapp_cliente ?? null,
        contato_email: log.email_cliente ?? null,
        contexto: contextoDe([
            ['Tipo', log.tipo_cliente],
            // Sem isto, manda-se o terceiro lembrete sem saber dos dois anteriores.
            ['Lembrete enviado', diaBR(log.lembrete_enviado_em)],
        ]),
        bruto: log,
    };

    if (!log.assinado_em) {
        const fila: EsteiraFila = tipo === 'mora' ? 'mora_sem_assinatura' : 'advertencia_sem_assinatura';
        return {
            ...base,
            fila,
            grupo: GRUPO_DA_FILA[fila],
            referencia_em: log.created_at,
            prazo_em: null,
            dias_parado: diasDesdeISO(log.created_at, agora),
            detalhe: `${rotulo} gerada e ainda não assinada pelo procurador.`,
        };
    }

    // "Não enviado" é um desfecho registrado (com motivo), não um esquecimento —
    // por isso não cai na fila de envio pendente.
    if (!log.enviado_cliente_em && !log.nao_enviado_cliente_em) {
        const fila: EsteiraFila = tipo === 'mora' ? 'mora_sem_envio' : 'advertencia_sem_envio';
        return {
            ...base,
            fila,
            grupo: GRUPO_DA_FILA[fila],
            referencia_em: log.assinado_em,
            prazo_em: null,
            dias_parado: diasDesdeISO(log.assinado_em, agora),
            detalhe: 'Assinada, mas o envio ao cliente nunca foi registrado — é o envio que inicia o prazo.',
        };
    }

    if (log.enviado_cliente_em && prazoVencido(log.prazo_final, agora) && !log.resultado_verificacao) {
        const fila: EsteiraFila = tipo === 'mora' ? 'mora_prazo_vencido' : 'advertencia_prazo_vencido';
        return {
            ...base,
            fila,
            grupo: GRUPO_DA_FILA[fila],
            referencia_em: log.prazo_final as string,
            prazo_em: log.prazo_final ?? null,
            dias_parado: diasDesdeISO(log.prazo_final, agora),
            detalhe: 'Prazo encerrado sem conferência — verifique o pagamento e registre o desfecho.',
        };
    }

    return null;
}

/**
 * Colunas do termo de cancelamento (distrato).
 *
 * ⚠️ Esta tabela NÃO tem `assinado_em`, `enviado_cliente_em` nem `prazo_final` —
 * diferente da mora e da advertência, a assinatura do distrato só existe no
 * Autentique e nunca foi espelhada no banco. Por isso a fila mostra "em aberto"
 * (gerado e não cancelado) em vez de um estado de assinatura que não temos.
 * Espelhar essas colunas é item da fase de SQL.
 */
export interface LogDistrato {
    id: number | string;
    uc_numero?: string | null;
    cliente_nome?: string | null;
    url_pdf?: string | null;
    created_at: string;
    user_id?: string | null;
    id_autentique?: string | null;
    modelo_distrato?: string | null;
    cancelado_em?: string | null;
    tipo_cliente?: string | null;
    motivo_desligamento?: string | null;
    observacoes?: string | null;
    // Espelho do Autentique, preenchido por `reconciliar-distrato`.
    assinado_em?: string | null;
    enviado_cliente_em?: string | null;
    nao_enviado_cliente_em?: string | null;
    assinaturas_total?: number | null;
    assinaturas_feitas?: number | null;
    assinantes_pendentes?: Array<{ email?: string | null; nome?: string | null; interno?: boolean }> | null;
    reconciliado_em?: string | null;
    // Devolução do e-mail do cliente, também gravada por `reconciliar-distrato`.
    email_cliente?: string | null;
    email_recusado_em?: string | null;
}

/** Primeiro nome, para a lista de quem falta caber no card. */
function primeiroNome(p: { nome?: string | null; email?: string | null }): string {
    if (p.nome?.trim()) return p.nome.trim().split(/\s+/)[0];
    return (p.email || '').split('@')[0] || 'alguém';
}

/** 'padrao' e 'inadimplencia' são processos diferentes — o rótulo precisa dizer. */
const MODELO_DISTRATO: Record<string, string> = {
    padrao: 'Distrato padrão',
    inadimplencia: 'Distrato por inadimplência',
};

export function normalizarDistrato(log: LogDistrato, agora: Date): DemandaEsteira | null {
    if (log.cancelado_em) return null;
    // Enviado ao cliente (ou marcado como não-envio) encerra o distrato: não há
    // mais nada a fazer com ele.
    if (log.enviado_cliente_em || log.nao_enviado_cliente_em) return null;

    const pendentes = Array.isArray(log.assinantes_pendentes) ? log.assinantes_pendentes : [];
    const faltaVigor = pendentes.filter(p => p.interno);
    const faltaCliente = pendentes.filter(p => !p.interno);

    /**
     * A fila sai do espelho do Autentique. Sem reconciliação ainda (termo
     * recém-gerado, cron não passou), cai no estado genérico — é melhor um
     * card sem detalhe do que um distrato invisível.
     *
     * Quando falta gente dos DOIS lados, a fila é a da Vigor: a assinatura
     * interna é a que se resolve hoje, e é o gargalo real — 52 dos 87 em
     * 11/09/2026 esperavam alguém de casa.
     */
    const fila: EsteiraFila =
        !log.reconciliado_em ? 'distrato_em_aberto'
            : log.assinado_em ? 'distrato_assinado_sem_envio'
                : faltaVigor.length ? 'distrato_aguardando_vigor'
                    : faltaCliente.length ? 'distrato_aguardando_cliente'
                        : 'distrato_em_aberto';

    // Assinado e não enviado: o relógio recomeça na assinatura, igual à mora.
    const referencia = fila === 'distrato_assinado_sem_envio' && log.assinado_em
        ? log.assinado_em
        : log.created_at;

    const detalhe =
        fila === 'distrato_assinado_sem_envio'
            ? 'Todas as assinaturas saíram. Falta enviar o termo ao cliente e registrar o envio.'
            : fila === 'distrato_aguardando_vigor'
                ? `Falta assinatura interna: ${faltaVigor.map(primeiroNome).join(', ')}.`
                + (faltaCliente.length ? ' Depois dela, ainda falta o cliente.' : ' É a única coisa que trava — resolve internamente.')
                : fila === 'distrato_aguardando_cliente'
                    ? `Aguardando o cliente assinar (${faltaCliente.map(primeiroNome).join(', ')}).`
                    // O e-mail voltou, mas o WhatsApp foi: cobrar pelo WhatsApp,
                    // e corrigir o e-mail no cadastro para o próximo documento.
                    + (log.email_recusado_em ? ' O e-mail dele voltou — o link chegou só pelo WhatsApp.' : '')
                    : log.id_autentique
                        ? 'Ainda não conferido no Autentique — a reconciliação preenche no próximo ciclo.'
                        : 'Termo gerado sem envio ao Autentique.';

    return {
        id: `termo_cancelamento_logs:${log.id}`,
        origem: 'termo_cancelamento_logs',
        origem_id: String(log.id),
        fila,
        grupo: GRUPO_DA_FILA[fila],
        cliente_nome: log.cliente_nome ?? null,
        cpf_cnpj_cliente: null,
        uc_numero: log.uc_numero ?? null,
        valor: null,
        criado_em: log.created_at,
        referencia_em: referencia,
        prazo_em: null,
        dias_parado: diasDesdeISO(referencia, agora),
        responsavel_id: log.user_id ?? null,
        status: 'pendente',
        url_documento: log.url_pdf ?? null,
        contexto: contextoDe([
            ['Assinaturas', log.assinaturas_total != null ? `${log.assinaturas_feitas ?? 0} de ${log.assinaturas_total}` : null],
            ['E-mail voltou', log.email_recusado_em ? `${diaBR(log.email_recusado_em)} (${log.email_cliente ?? 'e-mail do cliente'})` : null],
            // O modelo muda o processo inteiro: "padrão" é saída voluntária,
            // "inadimplência" é ação de cobrança. Está preenchido em 100% das
            // linhas e não aparecia em lugar nenhum.
            ['Modelo', log.modelo_distrato ? MODELO_DISTRATO[log.modelo_distrato] ?? log.modelo_distrato : null],
            ['Tipo', log.tipo_cliente],
            ['Motivo', log.motivo_desligamento],
            ['Observações', log.observacoes],
        ]),
        detalhe,
        bruto: log,
    };
}

/** Colunas de `cobranca_clientes` que a esteira lê. */
export interface LinhaCobrancaCliente {
    id: string;
    cpf_cnpj_cliente: string;
    cliente_nome?: string | null;
    status_negociacao: string;
    valor_negociado?: number | null;
    proximo_contato_em?: string | null;
    negativado_em?: string | null;
    negativado_por_nome?: string | null;
    negativacao_realizada_em?: string | null;
    negativacao_realizada_por_nome?: string | null;
    negativacao_retirada_em?: string | null;
    responsavel_cobranca_id?: string | null;
    data_pagamento_negociado?: string | null;
    pago_em?: string | null;
    notificacao_encaminhada_em?: string | null;
    notificacao_encaminhada_por_nome?: string | null;
    created_at: string;
    updated_at?: string | null;
}

/**
 * Débito vencido de um cliente hoje — o que a função do banco
 * `cobranca_debito_por_cliente` devolve: fatura vencida, em aberto e fora de
 * acordo de parcelamento.
 */
export interface DebitoCliente {
    faturas: number[];
    valor: number;
    /** 'YYYY-MM-DD' da fatura vencida mais antiga. */
    vencimento_mais_antigo?: string | null;
    /** 'YYYY-MM-DD' da fatura vencida mais recente — diz se a dívida é nova. */
    vencimento_mais_recente?: string | null;
    /** As faturas acima que são de UC com distrato vivo. */
    faturas_distrato?: number[];
    /** Nome do cliente, para quem deve e ainda não tem linha de cobrança. */
    cliente_nome?: string | null;
}

/** Último contato registrado com o cliente (`cobranca_negociacao_historico`, tipo 'contato'). */
export interface UltimoContato {
    /** ISO — `contato_em`, ou `created_at` quando não houver. */
    em: string;
    por: string | null;
    /** Quem registrou — vira o dono do cliente sem carteira (#6211). */
    user_id?: string | null;
}

/** Até quantos dias depois do último contato quem registrou vale como dono (#6211). */
const DIAS_DONO_PELO_CONTATO = 60;

/** Status em que a cobrança já acabou — nada a fazer, sai da esteira. */
const STATUS_ENCERRADOS = ['pago', 'negativacao_realizada'];

/**
 * Status em que o cliente segue sendo cobrado por quem tem a carteira — e,
 * portanto, precisa de data para voltar a falar com ele (#6165). Negociado tem
 * a própria data (dia seguinte ao combinado); pago e negativação não voltam.
 */
const STATUS_EM_COBRANCA = ['aguardando_contato', 'negociando', 'sem_pagamento', 'notificacao_realizada'];

/**
 * Dívida que venceu DEPOIS de a cobrança ter sido encerrada como paga. "Pago"
 * fechava o cliente para sempre: em 07/10/2026, 8 clientes marcados como pagos
 * tinham fatura nova vencida e não apareciam para ninguém. A data é a do
 * recebimento confirmado — sem ela não dá para separar dívida velha de nova, e
 * o cliente continua encerrado.
 */
export function temDividaNovaDepoisDoPago(
    linha: Pick<LinhaCobrancaCliente, 'status_negociacao' | 'pago_em'>,
    debito?: DebitoCliente | null,
): boolean {
    if (linha.status_negociacao !== 'pago' || !linha.pago_em) return false;
    const pagoEm = diaLocal(linha.pago_em);
    const maisRecente = debito?.vencimento_mais_recente;
    return !!pagoEm && !!maisRecente && !!debito?.faturas.length && maisRecente > pagoEm;
}

/** Dia de atraso do primeiro contato humano da régua (D+4, #6145). */
const PRIMEIRO_CONTATO_HUMANO = 4;

/** Cliente regularizado: pagou ou fechou acordo. */
const STATUS_REGULARIZADO = ['pago', 'negociado'];

/**
 * Uma linha de cobrança vira NO MÁXIMO uma demanda, na ordem de urgência abaixo.
 * Sem isso o mesmo cliente apareceria três vezes na tela — sem responsável, com
 * follow-up vencido e com negativação pendente são estados que convivem.
 *
 * `debito` diz se o cliente ainda deve alguma coisa vencida:
 *   • `undefined` — não deu para saber (a consulta falhou): nada é escondido,
 *     a tela se comporta como antes;
 *   • `null` — não deve nada;
 *   • objeto — deve estas faturas.
 * Antes não havia essa informação e 412 dos 480 cards "sem responsável" eram
 * de clientes que não deviam nada (medido em 01/10/2026, #6147).
 *
 * `ultimoContato` (opcional): o contato mais recente registrado. Sem ele —
 * consulta falhou — a fila "Sem próximo contato" não é montada.
 *
 * `notificacaoGeradaEm` (opcional): quando foi gerada a notificação de mora
 * mais recente deste CPF. Tira da fila "encaminhada" quem já foi notificado
 * depois do encaminhamento — dali em diante o documento tem fila própria.
 */
export function normalizarCobrancaCliente(
    linha: LinhaCobrancaCliente,
    agora: Date,
    debito?: DebitoCliente | null,
    ultimoContato?: UltimoContato | null,
    notificacaoGeradaEm?: string | null,
): DemandaEsteira | null {
    const sabeODebito = debito !== undefined;
    const deve = !sabeODebito || !!debito?.faturas.length;
    const pagoComDividaNova = temDividaNovaDepoisDoPago(linha, debito);
    // Quem segue sendo cobrado pela carteira. Negociado com o acordo vencido
    // também: o combinado não foi pago e ninguém marcou retorno.
    const hoje = diaLocal(agora.toISOString());
    const acordoVencido = linha.status_negociacao === 'negociado'
        && (!linha.data_pagamento_negociado || (!!hoje && linha.data_pagamento_negociado < hoje));
    const emCobranca = STATUS_EM_COBRANCA.includes(linha.status_negociacao) || pagoComDividaNova || acordoVencido;

    // Dono pelo contato só com contato recente: quem falou com o cliente há meses
    // (ou saiu da empresa) não segura o card — senão o abandono some do "Sem dono".
    const contatoRecente = !!ultimoContato && diasDesdeISO(ultimoContato.em, agora) <= DIAS_DONO_PELO_CONTATO;
    const dono = linha.responsavel_cobranca_id || (contatoRecente ? ultimoContato?.user_id : null) || null;
    const base = {
        origem: 'cobranca_clientes' as const,
        origem_id: linha.id,
        id: `cobranca_clientes:${linha.id}`,
        // Nome de quem deve hoje (#6306): o guardado na cobrança é o do primeiro
        // cliente visto com o documento e não acompanha o cadastro.
        cliente_nome: debito?.cliente_nome || linha.cliente_nome || null,
        cpf_cnpj_cliente: linha.cpf_cnpj_cliente,
        uc_numero: null,
        // Defensivo: `valor_negociado` é numeric hoje, mas a mora ensinou que
        // coluna de dinheiro neste schema nem sempre é o que o nome promete.
        valor: parseValorBR(linha.valor_negociado),
        criado_em: linha.created_at,
        prazo_em: null,
        // Dono da carteira do cliente — é dono de verdade, não quem gerou algo.
        // Dono da carteira; sem carteira, quem registrou o último contato (#6211:
        // cliente em negociação aparecia "sem dono" com alguém cuidando dele). Só
        // na tela — gravar na carteira dispararia a régua e o aviso de retorno.
        responsavel_id: dono,
        responsavel_nome: !linha.responsavel_cobranca_id && dono ? ultimoContato?.por ?? null : undefined,
        dono_pelo_contato: !linha.responsavel_cobranca_id && !!dono,
        status: 'pendente' as const,
        url_documento: null,
        proximo_contato_em: linha.proximo_contato_em ?? null,
        // Negociar com o nome no SPC é normal: o selo acompanha qualquer fila.
        negativado_spc_em: linha.negativacao_realizada_em && !linha.negativacao_retirada_em
            ? linha.negativacao_realizada_em : null,
        bruto: linha,
    };

    // Negativado que pagou ou negociou: o nome continua sujo no SPC até alguém
    // tirar. Vem antes de tudo — é obrigação com prazo, não cobrança.
    const regularizou = STATUS_REGULARIZADO.includes(linha.status_negociacao) || (sabeODebito && !deve);
    if (linha.negativacao_realizada_em && !linha.negativacao_retirada_em && regularizou) {
        const referencia = linha.pago_em || linha.updated_at || linha.negativacao_realizada_em;
        return {
            ...base,
            fila: 'negativacao_retirar',
            grupo: GRUPO_DA_FILA.negativacao_retirar,
            referencia_em: referencia,
            dias_parado: diasDesdeISO(referencia, agora),
            contexto: contextoDe([
                ['Negativado em', diaBR(linha.negativacao_realizada_em)],
                ['Negativado por', linha.negativacao_realizada_por_nome],
                ['Situação', linha.status_negociacao === 'pago' ? 'pagou'
                    : linha.status_negociacao === 'negociado' ? 'fechou acordo'
                        : 'não tem mais débito vencido'],
            ]),
            detalhe: 'O cliente regularizou, mas o nome dele continua negativado. Retire a negativação no SPC e registre.',
        };
    }

    // Negativado e ainda devendo: não é tarefa, mas a cobrança precisa ver quem
    // está no SPC — eram 102 e o painel não mostrava nenhum (#6209).
    const negativado = (): DemandaEsteira => ({
        ...base,
        fila: 'negativado_spc',
        grupo: GRUPO_DA_FILA.negativado_spc,
        valor: debito?.faturas.length ? debito.valor : base.valor,
        referencia_em: linha.negativacao_realizada_em as string,
        dias_parado: diasDesdeISO(linha.negativacao_realizada_em, agora),
        acompanhamento: true,
        contexto: contextoDe([
            ['Negativado em', diaBR(linha.negativacao_realizada_em)],
            ['Negativado por', linha.negativacao_realizada_por_nome],
            ['Vencida desde', diaBR(debito?.vencimento_mais_antigo)],
        ]),
        detalhe: 'Negativado no SPC e ainda devendo. Quando pagar ou fechar acordo, passa para "Retirar negativação".',
    });
    const estaNegativado = !!linha.negativacao_realizada_em && !linha.negativacao_retirada_em;

    if (STATUS_ENCERRADOS.includes(linha.status_negociacao) && !pagoComDividaNova) {
        return linha.status_negociacao === 'negativacao_realizada' && estaNegativado ? negativado() : null;
    }

    // Só quem ESTÁ marcado para negativar. Antes bastava ter `negativado_em`:
    // cliente que negociou depois de marcado continuava como "negativação
    // pendente" para sempre (Gilmar e Elizangela, nos prints do #6147).
    if (linha.status_negociacao === 'negativacao_pendente' && !linha.negativacao_realizada_em) {
        const referencia = linha.negativado_em || linha.created_at;
        return {
            ...base,
            fila: 'negativacao_pendente',
            grupo: GRUPO_DA_FILA.negativacao_pendente,
            referencia_em: referencia,
            dias_parado: diasDesdeISO(referencia, agora),
            // Quem efetiva no SPC precisa saber quem decidiu — é decisão com
            // consequência para o cliente, não tarefa anônima.
            contexto: contextoDe([
                ['Marcado por', linha.negativado_por_nome],
                ['Marcado em', diaBR(linha.negativado_em)],
            ]),
            detalhe: deve
                ? 'Marcado para negativar, mas a negativação no SPC ainda não foi feita.'
                : 'Marcado para negativar, mas o cliente não tem mais débito vencido — confira antes de negativar.',
        };
    }

    // Encaminhado para a notificação extrajudicial pelo "Gerenciar Cobrança"
    // (pedido da Dhaiane, 07/10/2026). Fica com quem notifica até a notificação
    // ser gerada; depois disso o documento tem fila própria. Não espera retorno
    // agendado — ver `marcarAgendados`.
    if (linha.status_negociacao === 'notificacao_extrajudicial') {
        const encaminhadaEm = linha.notificacao_encaminhada_em || linha.updated_at || linha.created_at;
        const jaNotificado = !!notificacaoGeradaEm && Date.parse(notificacaoGeradaEm) >= Date.parse(encaminhadaEm);
        if (jaNotificado || !deve) return null;
        return {
            ...base,
            fila: 'notificacao_extrajudicial',
            grupo: GRUPO_DA_FILA.notificacao_extrajudicial,
            valor: debito?.faturas.length ? debito.valor : base.valor,
            referencia_em: encaminhadaEm,
            dias_parado: diasDesdeISO(encaminhadaEm, agora),
            contexto: contextoDe([
                ['Encaminhado por', linha.notificacao_encaminhada_por_nome],
                ['Encaminhado em', diaBR(linha.notificacao_encaminhada_em)],
                ['Vencida desde', diaBR(debito?.vencimento_mais_antigo)],
            ]),
            detalhe: 'Encaminhado para a notificação extrajudicial. Gere a notificação e registre o envio ao cliente.',
        };
    }

    // Teve contato e ficou sem o próximo passo (#6165). Até 05/10/2026 dava para
    // registrar o contato sem data: o Daniel Carvalho teve contato em 29/09 e o
    // card seguiu "retorno vencido há 67 dias", pela data de 30/07 que ninguém
    // trocou. A idade conta do último contato — foi quando alguém mexeu.
    // O que ele deve hoje. Sem isto o card de retorno mostrava o valor do acordo
    // (quase sempre vazio) e parecia de quem não deve nada (reunião de 23/10/2026).
    const valorDevido = debito?.faturas.length ? debito.valor : base.valor;

    const semRetorno = (contato: UltimoContato, retornoAntigo: string | null): DemandaEsteira => ({
        ...base,
        valor: valorDevido,
        fila: 'sem_retorno',
        grupo: GRUPO_DA_FILA.sem_retorno,
        referencia_em: contato.em,
        dias_parado: diasDesdeISO(contato.em, agora),
        contexto: contextoDe([
            ['Último contato', diaBR(diaLocal(contato.em))],
            ['Por', contato.por],
            ['Retorno que estava marcado', diaBR(retornoAntigo)],
        ]),
        detalhe: 'Teve contato, mas ninguém marcou quando voltar a falar com o cliente. Registre o contato com a data do retorno ou com o status.',
    });

    // Retorno agendado: volta NO DIA combinado, não só depois dele — é o
    // "voltar ao painel na data do próximo contato" do pedido. Cliente que não
    // deve mais nada não precisa de retorno de cobrança.
    if (linha.proximo_contato_em && deve && diasDesdeISO(linha.proximo_contato_em, agora) >= 0) {
        // Se a data foi digitada errada, contar a idade a partir dela põe esta
        // linha no topo da fila para sempre. Nesse caso a idade vem da criação
        // do registro, e o card avisa que a data precisa de conserto.
        const confiavel = dataPlausivel(linha.proximo_contato_em);
        // O retorno aconteceu (contato no dia ou depois) e ficou sem data nova.
        if (confiavel && ultimoContato && diasEntre(ultimoContato.em, linha.proximo_contato_em) >= 0) {
            return semRetorno(ultimoContato, linha.proximo_contato_em);
        }
        const referencia = confiavel ? linha.proximo_contato_em : linha.created_at;
        const dias = diasDesdeISO(referencia, agora);
        return {
            ...base,
            valor: valorDevido,
            fila: 'followup_vencido',
            grupo: GRUPO_DA_FILA.followup_vencido,
            referencia_em: referencia,
            prazo_em: confiavel ? linha.proximo_contato_em : null,
            dias_parado: dias,
            // Retomar o contato sem saber o que já foi combinado é recomeçar do
            // zero na frente do cliente.
            contexto: contextoDe([
                ['Pagamento combinado', diaBR(linha.data_pagamento_negociado)],
            ]),
            detalhe: !confiavel
                ? `Retorno vencido, mas a data registrada (${linha.proximo_contato_em}) é inválida — corrija ao retomar o contato.`
                : dias === 0
                    ? 'Hoje é o dia combinado para voltar a falar com o cliente.'
                    : `O retorno combinado com o cliente venceu há ${dias} dia${dias === 1 ? '' : 's'}.`,
        };
    }

    // Deve, tem dono, nenhuma data — e alguém falou com ele por causa DESTA
    // dívida. Contato de um atraso antigo não conta: o atraso novo é da régua
    // (D+4). Só com o débito conhecido: sem ele, todo cliente que um dia teve
    // contato apareceria aqui.
    if (!linha.proximo_contato_em && dono && ultimoContato
        && emCobranca
        && debito?.faturas.length
        && (!debito.vencimento_mais_antigo || diasEntre(ultimoContato.em, debito.vencimento_mais_antigo) >= 0)) {
        return semRetorno(ultimoContato, null);
    }

    // Antes do 4º dia o contato ainda é dos avisos automáticos (reunião de
    // 23/10/2026): o cliente sem dono aparecia no dia seguinte ao vencimento e
    // reclamava "venceu ontem e já estão me cobrando".
    const antesDoContatoHumano = !!debito?.vencimento_mais_antigo
        && diasDesdeISO(debito.vencimento_mais_antigo, agora) < PRIMEIRO_CONTATO_HUMANO;
    if (!dono && deve && !antesDoContatoHumano) {
        return {
            ...base,
            fila: 'sem_responsavel',
            grupo: GRUPO_DA_FILA.sem_responsavel,
            // O que ele deve hoje — o valor negociado, quando existe, é de um
            // acordo antigo.
            valor: valorDevido,
            referencia_em: linha.created_at,
            dias_parado: diasDesdeISO(linha.created_at, agora),
            contexto: contextoDe([
                ['Vencida desde', diaBR(debito?.vencimento_mais_antigo)],
                ['Faturas vencidas', debito?.faturas.length || null],
            ]),
            detalhe: 'Cobrança em aberto sem ninguém responsável.',
        };
    }

    // Deve, tem dono e ninguém falou com ele por esta dívida (07/10/2026). A
    // régua só cria etapa no 4º, 10º, 17º e 24º dia de atraso: quem passou
    // disso sem contato — ou voltou a dever depois de pagar — não aparecia
    // para ninguém. Com retorno marcado para depois de hoje, o card nasce e
    // `marcarAgendados` o guarda até a data.
    // Antes do 4º dia de atraso o contato ainda é dos avisos automáticos
    // (D-1, D+3) — o primeiro humano é o D+4.
    if (dono && emCobranca && debito?.faturas.length
        && diasDesdeISO(debito.vencimento_mais_antigo, agora) >= PRIMEIRO_CONTATO_HUMANO) {
        const desde = debito.vencimento_mais_antigo || linha.created_at;
        return {
            ...base,
            fila: 'sem_contato',
            grupo: GRUPO_DA_FILA.sem_contato,
            valor: debito.valor,
            referencia_em: desde,
            dias_parado: diasDesdeISO(desde, agora),
            contexto: contextoDe([
                ['Vencida desde', diaBR(debito.vencimento_mais_antigo)],
                ['Faturas vencidas', debito.faturas.length],
                ['Último contato', ultimoContato ? diaBR(diaLocal(ultimoContato.em)) : null],
                ['Pago em', pagoComDividaNova ? diaBR(diaLocal(linha.pago_em)) : null],
                ['Pagamento combinado', acordoVencido ? diaBR(linha.data_pagamento_negociado) : null],
            ]),
            detalhe: pagoComDividaNova
                ? 'Pagou a dívida anterior, mas tem fatura nova vencida. Entre em contato e registre como ficou.'
                : acordoVencido
                    ? 'O acordo venceu sem pagamento e ninguém marcou retorno. Entre em contato e registre como ficou.'
                    : linha.status_negociacao === 'notificacao_realizada'
                        ? 'Já foi notificado e segue devendo, sem próximo passo marcado. Negocie, marque o retorno ou mande negativar.'
                        : 'Tem fatura vencida e ninguém falou com o cliente por esta dívida. Entre em contato e registre como ficou.',
        };
    }

    return estaNegativado ? negativado() : null;
}

/**
 * Cliente que deve e ainda não tem linha em `cobranca_clientes` — nunca foi
 * aberto no "Gerenciar Cobrança" nem teve contato registrado. A linha é criada
 * na primeira ação (assumir, registrar contato); até lá o card nasce desta
 * linha provisória, com a mesma regra dos demais.
 */
export function linhaDeQuemSoDeve(cpf: string, debito: DebitoCliente): LinhaCobrancaCliente {
    return {
        id: `cpf:${cpf}`,
        cpf_cnpj_cliente: cpf,
        cliente_nome: debito.cliente_nome ?? null,
        status_negociacao: 'aguardando_contato',
        responsavel_cobranca_id: null,
        proximo_contato_em: null,
        created_at: debito.vencimento_mais_antigo || new Date().toISOString(),
    };
}

/**
 * Filas que o distrato tira da rotina de cobrança. Negativação fica: pôr e
 * tirar do SPC é obrigação com prazo, decidida por alguém, com ou sem
 * distrato. A notificação encaminhada também — foi escolha explícita.
 */
const FILAS_QUE_O_DISTRATO_ESCONDE: EsteiraFila[] = [
    'regua_d4', 'regua_d10', 'regua_d17', 'regua_d24',
    'followup_vencido', 'sem_retorno', 'sem_contato', 'sem_responsavel',
];

/**
 * Marca o que é do distrato (pedido da Dhaiane, 07/10/2026): o card do próprio
 * termo e a cobrança cujas faturas são TODAS de UC com distrato vivo. Quem tem
 * distrato numa UC e deve em outra continua na cobrança — essa dívida não está
 * no termo.
 *
 * `faturasDistrato` vem da função de débito do banco; `null` quando ela não
 * informou (versão antiga ou consulta falhou) — aí só o termo é marcado.
 */
export function marcarDistrato(
    d: DemandaEsteira,
    faturasDistrato: Set<number> | null,
    debito?: DebitoCliente | null,
): DemandaEsteira {
    if (d.origem === 'termo_cancelamento_logs') return { ...d, com_distrato: true };
    if (!faturasDistrato?.size || !FILAS_QUE_O_DISTRATO_ESCONDE.includes(d.fila)) return d;

    const faturas = d.origem === 'cobranca_acao_manual'
        ? ((d.bruto as CobrancaAcaoManual | undefined)?.faturas ?? []).map(f => Number(f.fatura_id))
        : d.origem === 'cobranca_clientes' ? (debito?.faturas ?? []) : [];
    if (!faturas.length) return d;
    return faturas.every(f => faturasDistrato.has(f)) ? { ...d, com_distrato: true } : d;
}

const FILA_DA_ETAPA: Record<string, EsteiraFila> = {
    'D+4': 'regua_d4',
    'D+10': 'regua_d10',
    'D+17': 'regua_d17',
    'D+24': 'regua_d24',
};

/**
 * A régua de contato humano entra inteira, inclusive concluídos e dispensados —
 * o filtro de status da tela decide o que mostrar, igual à ManuaisTab.
 */
export function normalizarAcaoManual(acao: CobrancaAcaoManual, agora: Date): DemandaEsteira | null {
    const fila = FILA_DA_ETAPA[acao.etapa];
    if (!fila) return null;

    return {
        id: `cobranca_acao_manual:${acao.id}`,
        origem: 'cobranca_acao_manual',
        origem_id: acao.id,
        fila,
        grupo: GRUPO_DA_FILA[fila],
        cliente_nome: acao.cliente_nome,
        cpf_cnpj_cliente: acao.cpf_cnpj_cliente,
        // Todas as UCs da etapa: com só a primeira o card perdia o "+N" (#6201).
        ucs: escolherUcsDoCliente((acao.faturas ?? []).map(f => f.uc_codigo), []),
        uc_numero: escolherUcsDoCliente((acao.faturas ?? []).map(f => f.uc_codigo), [])[0]?.numero ?? null,
        valor: parseValorBR(acao.valor_total),
        criado_em: acao.created_at,
        referencia_em: acao.data_referencia,
        prazo_em: null,
        dias_parado: diasDesdeISO(acao.data_referencia, agora),
        responsavel_id: acao.responsavel_id,
        responsavel_nome: acao.responsavel_nome ?? null,
        status: acao.status,
        url_documento: null,
        contato_whatsapp: acao.whatsapp_destino,
        contato_email: null,
        detalhe: null,
        bruto: acao,
    };
}

export type OrdemPainel = 'recente' | 'antigo';

/**
 * Idade que ordena o card: o atraso da dívida, não o tempo desde o último contato
 * nem desde o retorno marcado. Com os débitos no card, conta da fatura vencida
 * mais antiga — o retorno de 2 dias de quem deve há 200 abria a lista (25/10/2026).
 */
const idadeDoCard = (d: DemandaEsteira, agora: Date): number => {
    const vencimentos = (d.faturas_abertas ?? []).map(f => f.vencimento).filter((v): v is string => !!v).sort();
    return vencimentos.length ? diasDesdeISO(vencimentos[0], agora) : d.dias_atraso ?? d.dias_parado;
};

/**
 * Ordem da fila. Padrão desde a reunião de 23/10/2026 (Dhaiane): quem ficou
 * inadimplente há menos tempo primeiro — é quem mais paga quando a gente liga.
 * A Andriele, devendo desde 2025, abria a lista e o atraso de um mês ficava lá
 * embaixo. "antigo" é a ordem de antes: o mais parado primeiro. No empate, o de
 * maior valor.
 */
export function ordenarDemandas(demandas: DemandaEsteira[], ordem: OrdemPainel = 'recente', agora: Date = new Date()): DemandaEsteira[] {
    const idade = new Map(demandas.map(d => [d, idadeDoCard(d, agora)]));
    // Acompanhamento (negativado) vai para o fim: não é trabalho a fazer.
    return [...demandas].sort((a, b) =>
        Number(!!a.acompanhamento) - Number(!!b.acompanhamento)
        || (ordem === 'antigo' ? b.dias_parado - a.dias_parado : idade.get(a)! - idade.get(b)!)
        || (b.valor ?? 0) - (a.valor ?? 0)
    );
}

/**
 * Tira da lista a etapa da régua cujas faturas não estão mais vencidas e em
 * aberto — paga, baixada, em acordo ou reemitida com vencimento novo.
 *
 * A rotina do banco só faz essa limpeza uma vez por dia, às 8h05: pago às 10h
 * continuava cobrando até a manhã seguinte. `faturasVencidas` é o conjunto de
 * TODAS as faturas vencidas em aberto (de `cobranca_debito_por_cliente`); `null`
 * quando a consulta falhou — aí nada é escondido.
 *
 * Só olha pendente: concluída e dispensada aparecem quando o filtro pede. Etapa
 * sem fatura no registro fica, para não sumir em silêncio.
 *
 * `faturasPorCpf` (opcional): as faturas vencidas de cada CPF. Com ele, a etapa
 * só fica se a fatura ainda é dívida DESTE cliente — a etapa criada no CPF do
 * dono novo da UC continuava no card dele depois que a dívida voltou para o
 * antigo (Ronaldo Bezerra x Viviane, 24/10/2026). Etapa sem CPF segue a regra geral.
 */
export function filtrarReguaComDebito(
    demandas: DemandaEsteira[],
    faturasVencidas: Set<number> | null,
    faturasPorCpf?: Map<string, Set<number>> | null,
): DemandaEsteira[] {
    if (!faturasVencidas) return demandas;
    return demandas.filter(d => {
        if (d.origem !== 'cobranca_acao_manual' || d.status !== 'pendente') return true;
        const faturas = (d.bruto as CobrancaAcaoManual | undefined)?.faturas ?? [];
        if (!faturas.length) return true;
        const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
        const doCliente = cpf && faturasPorCpf ? faturasPorCpf.get(cpf) ?? new Set<number>() : faturasVencidas;
        return faturas.some(f => doCliente.has(Number(f.fatura_id)));
    });
}

/** Nome para comparar pessoas: maiúsculo, sem acento, espaços simples. */
export const nomeComparavel = (nome: string | null | undefined): string =>
    String(nome ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();

/**
 * Dívida vencida por nome de devedor, para achar a mesma pessoa em outro CPF.
 * O débito junta nomes com " / " quando o CPF paga UCs de nomes diferentes.
 */
export function dividaPorNome(debitos: Iterable<[string, DebitoCliente]>): Map<string, { valor: number; faturas: number; cpfs: Set<string> }> {
    const mapa = new Map<string, { valor: number; faturas: number; cpfs: Set<string> }>();
    for (const [cpf, d] of debitos) {
        if (!d.faturas.length) continue;
        for (const nome of String(d.cliente_nome ?? '').split(' / ').map(nomeComparavel).filter(Boolean)) {
            const atual = mapa.get(nome) ?? { valor: 0, faturas: 0, cpfs: new Set<string>() };
            atual.valor += d.valor;
            atual.faturas += d.faturas.length;
            atual.cpfs.add(cpf.replace(/\D/g, ''));
            mapa.set(nome, atual);
        }
    }
    return mapa;
}

/**
 * "Retirar negativação" de quem só parece não dever (25/10/2026). O cadastro
 * antigo põe o CPF do titular na UC e a negativação foi feita no CPF do boleto:
 * a dívida fica num CPF e o SPC no outro. Eram 7 clientes devendo de R$ 249 a
 * R$ 10 mil (Estela Miranda, Fabio Aurelio...) com o painel mandando tirar do
 * SPC. Com dívida vencida no MESMO nome em outro CPF, o card vira acompanhamento
 * e diz quanto ele deve — retirar fica para quem conferir.
 *
 * Pagou ou fechou acordo (status) continua pedindo a retirada: aí a dívida
 * acabou de verdade.
 */
export function marcarDividaEmOutroCpf(
    d: DemandaEsteira,
    porNome: Map<string, { valor: number; faturas: number; cpfs: Set<string> }>,
): DemandaEsteira {
    if (d.fila !== 'negativacao_retirar') return d;
    const status = (d.bruto as LinhaCobrancaCliente | undefined)?.status_negociacao;
    if (status && STATUS_REGULARIZADO.includes(status)) return d;
    const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
    const outra = porNome.get(nomeComparavel(d.cliente_nome));
    if (!outra || (cpf && outra.cpfs.has(cpf) && outra.cpfs.size === 1)) return d;
    const valor = Math.round(outra.valor * 100) / 100;
    return {
        ...d,
        fila: 'negativado_spc',
        grupo: GRUPO_DA_FILA.negativado_spc,
        valor,
        acompanhamento: true,
        contexto: [
            ...(d.contexto ?? []).filter(c => c.rotulo !== 'Situação'),
            { rotulo: 'Deve em outro CPF', valor: `${outra.faturas} fatura${outra.faturas === 1 ? '' : 's'}` },
        ],
        detalhe: 'Negativado e com fatura vencida no mesmo nome em outro CPF (cadastro da UC). Não retire a negativação sem conferir de quem é a dívida.',
    };
}

/**
 * Notificação de mora ainda não assinada ou não enviada de quem não deve mais
 * (reunião de 23/10/2026). A da ED Alimentos, gerada em 08/07 e nunca assinada,
 * abria a lista sem valor depois de o cliente fechar acordo e pagar a entrada.
 *
 * Decide pelas faturas da própria notificação; sem elas, pelo débito do CPF.
 * Sem saber o débito (`null`), nada some. A notificação enviada com prazo
 * vencido fica: o desfecho dela precisa ser registrado.
 */
export function filtrarMoraSemDebito(
    demandas: DemandaEsteira[],
    faturasVencidas: Set<number> | null,
    faturasPorCpf: Map<string, Set<number>> | null,
): DemandaEsteira[] {
    if (!faturasVencidas || !faturasPorCpf) return demandas;
    return demandas.filter(d => {
        if (d.fila !== 'mora_sem_assinatura' && d.fila !== 'mora_sem_envio') return true;
        const log = d.bruto as LogDocumento | undefined;
        const ids = (log?.fatura_ids ?? []).map(Number).filter(n => Number.isFinite(n));
        if (ids.length) return ids.some(id => faturasVencidas.has(id));
        const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
        if (!cpf) return true;
        return (faturasPorCpf.get(cpf)?.size ?? 0) > 0;
    });
}

/**
 * O que entra no card por cliente: a régua e a carteira do cliente. Documento é
 * outro trabalho (assinar, enviar) e fica um por linha.
 */
const ehCobrancaDoCliente = (d: DemandaEsteira): boolean =>
    d.origem === 'cobranca_acao_manual' || d.origem === 'cobranca_clientes';

/**
 * Qual fila manda no card quando ele junta várias. Primeiro o que tem prazo ou
 * consequência para o cliente (tirar e pôr no SPC), depois as etapas mais
 * avançadas da régua, e só então o contato.
 */
const PRIORIDADE_NO_CARD: EsteiraFila[] = [
    'negativacao_retirar', 'negativacao_pendente',
    'regua_d24', 'regua_d17', 'notificacao_extrajudicial',
    'followup_vencido', 'sem_retorno', 'regua_d10', 'regua_d4',
    'sem_contato', 'sem_responsavel', 'negativado_spc',
];

const prioridadeNoCard = (f: EsteiraFila): number => {
    const i = PRIORIDADE_NO_CARD.indexOf(f);
    return i === -1 ? PRIORIDADE_NO_CARD.length : i;
};

/** Chave do cliente: CPF/CNPJ só com dígitos ou, na régua sem CPF, a chave de UC. */
export function chaveDoCliente(d: DemandaEsteira): string | null {
    const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
    if (cpf) return cpf;
    if (d.origem === 'cobranca_acao_manual') {
        return (d.bruto as CobrancaAcaoManual | undefined)?.chave_agrupamento ?? null;
    }
    return null;
}

/**
 * UCs que o card do cliente mostra (#6201). O card de cobrança nasce do CPF e
 * não sabia a UC — a Meyryene precisava abrir o cliente para descobrir.
 *
 * Mostra as UCs que estão devendo (das faturas vencidas em aberto). Se o
 * cliente não deve nada — "retirar negativação", por exemplo —, as do cadastro
 * pelo CPF, as canceladas por último e marcadas. Sem cadastro com o CPF (a
 * Isabel Francisca tem 5 UCs, nenhuma com o CPF preenchido), as UCs das
 * faturas dela.
 */
export function escolherUcsDoCliente(
    ucsDevendo: Array<string | null | undefined>,
    cadastro: Array<{ numero_uc?: string | null; situacao?: string | null }>,
    ucsDasFaturas: Array<string | null | undefined> = [],
): Array<{ numero: string; cancelada?: boolean }> {
    const devendo = [...new Set(ucsDevendo.map(u => String(u ?? '').trim()).filter(Boolean))];
    if (devendo.length) return devendo.map(numero => ({ numero }));
    const vistas = new Set<string>();
    const lista: Array<{ numero: string; cancelada?: boolean }> = [];
    for (const u of cadastro) {
        const numero = String(u.numero_uc ?? '').trim();
        if (!numero || vistas.has(numero)) continue;
        vistas.add(numero);
        lista.push(/^cancelad/i.test(String(u.situacao ?? '')) ? { numero, cancelada: true } : { numero });
    }
    if (!lista.length) {
        return [...new Set(ucsDasFaturas.map(u => String(u ?? '').trim()).filter(Boolean))].map(numero => ({ numero }));
    }
    return lista.sort((a, b) => Number(!!a.cancelada) - Number(!!b.cancelada));
}

/** Põe as UCs no card que não tem UC própria (régua, negociação, negativação). */
export function comUcs(
    d: DemandaEsteira,
    ucsPorCpf: Map<string, Array<{ numero: string; cancelada?: boolean }>>,
): DemandaEsteira {
    if (d.uc_numero) return d.ucs ? d : { ...d, ucs: [{ numero: d.uc_numero }] };
    const ucs = ucsPorCpf.get((d.cpf_cnpj_cliente ?? '').replace(/\D/g, ''));
    return ucs?.length ? { ...d, ucs, uc_numero: ucs[0].numero } : d;
}

/** Todas as filas que o card cobre — a principal primeiro. */
export function filasDa(d: DemandaEsteira): EsteiraFila[] {
    return [d.fila, ...(d.outras_filas ?? [])];
}

/** UCs de todos os itens do card, sem repetir; `undefined` se nenhum tem. */
function unirUcs(itens: DemandaEsteira[]): DemandaEsteira['ucs'] {
    const porNumero = new Map<string, { numero: string; cancelada?: boolean }>();
    for (const u of itens.flatMap(i => i.ucs ?? (i.uc_numero ? [{ numero: i.uc_numero }] : []))) {
        if (!porNumero.has(u.numero)) porNumero.set(u.numero, u);
    }
    return porNumero.size ? [...porNumero.values()] : undefined;
}

/** Junta as pendências de um card num só. `chave` vira o id estável dele. */
function fundir(chave: string, itens: DemandaEsteira[]): DemandaEsteira {
    const ordenados = [...itens].sort((a, b) =>
        prioridadeNoCard(a.fila) - prioridadeNoCard(b.fila) || b.dias_parado - a.dias_parado
    );
    const principal = ordenados[0];
    if (ordenados.length === 1) return principal;

    // Faturas sem repetir: o mesmo boleto estava em até 4 etapas e a soma da
    // tela dava o dobro da dívida real (R$ 483 mil x R$ 239 mil, 01/10/2026).
    const regua = ordenados.filter(i => i.origem === 'cobranca_acao_manual');
    const faturas = new Map<number, CobrancaAcaoManual['faturas'][number]>();
    for (const r of regua) {
        for (const f of (r.bruto as CobrancaAcaoManual | undefined)?.faturas ?? []) {
            if (!faturas.has(Number(f.fatura_id))) faturas.set(Number(f.fatura_id), f);
        }
    }
    const listaFaturas = [...faturas.values()];
    const valorRegua = listaFaturas.reduce((s, f) => s + Number(f.valor ?? 0), 0);

    const maisAntiga = ordenados.reduce((a, b) => (b.dias_parado > a.dias_parado ? b : a));
    const outras = [...new Set(ordenados.map(i => i.fila))].filter(f => f !== principal.fila);
    const primeiro = <K extends keyof DemandaEsteira>(campo: K) =>
        ordenados.map(i => i[campo]).find(v => v != null) ?? null;

    // Responsável do card: o da carteira do cliente (o mesmo do "Gerenciar
    // Cobrança"); sem carteira, o da fila principal.
    const carteira = ordenados.find(i => i.origem === 'cobranca_clientes' && donoEfetivo(i));
    const dono = carteira ?? principal;
    const donoId = donoEfetivo(dono);
    const donosEtapas = new Map<string, { id: string; nome: string | null; filas: EsteiraFila[] }>();
    for (const i of ordenados) {
        const id = donoEfetivo(i);
        if (!id || id === donoId) continue;
        const atual = donosEtapas.get(id) ?? { id, nome: i.responsavel_nome ?? i.atribuicao?.responsavel_nome ?? null, filas: [] };
        if (!atual.filas.includes(i.fila)) atual.filas.push(i.fila);
        donosEtapas.set(id, atual);
    }

    return {
        ...principal,
        id: `cliente:${chave}`,
        responsavel_id: donoId,
        responsavel_nome: dono.responsavel_nome ?? dono.atribuicao?.responsavel_nome ?? null,
        // O dono inferido do contato continua marcado no card fundido (#6211):
        // senão "assumir" pedia para tirar uma carteira que não existe.
        dono_pelo_contato: dono.dono_pelo_contato ?? false,
        donos_etapas: donosEtapas.size ? [...donosEtapas.values()] : undefined,
        negativado_spc_em: primeiro('negativado_spc_em') as string | null,
        acompanhamento: ordenados.every(i => i.acompanhamento) || undefined,
        cliente_nome: principal.cliente_nome ?? (primeiro('cliente_nome') as string | null),
        cpf_cnpj_cliente: principal.cpf_cnpj_cliente ?? (primeiro('cpf_cnpj_cliente') as string | null),
        uc_numero: principal.uc_numero ?? (primeiro('uc_numero') as string | null),
        ucs: unirUcs(ordenados),
        valor: regua.length ? valorRegua : principal.valor,
        referencia_em: maisAntiga.referencia_em,
        dias_parado: maisAntiga.dias_parado,
        atribuicao: principal.atribuicao ?? (primeiro('atribuicao') as DemandaEsteira['atribuicao']),
        contato_whatsapp: principal.contato_whatsapp ?? (primeiro('contato_whatsapp') as string | null),
        contato_email: principal.contato_email ?? (primeiro('contato_email') as string | null),
        proximo_contato_em: principal.proximo_contato_em ?? (primeiro('proximo_contato_em') as string | null),
        // A régua guarda as faturas no registro bruto; o card e a rota leem
        // de lá. No card agrupado elas são a união, sem repetir.
        bruto: regua.length
            ? { ...(regua[0].bruto as object), faturas: listaFaturas }
            : principal.bruto,
        itens: ordenados,
        outras_filas: outras,
        // Só sai da lista se TUDO no card é do distrato.
        com_distrato: ordenados.every(i => i.com_distrato),
    };
}

/**
 * Um card por cliente (#6209). Até 09/10/2026 era um card por cliente E por
 * pessoa: a D+17 com a Bruna e o retorno com o João viravam dois cards da mesma
 * cliente, cada um com um responsável — a Bruna leu como duplicidade. Agora é
 * um card; o responsável é o da carteira, e quem tem etapa aparece como "D+17
 * com Bruna" (`donos_etapas`) e recebe o card em "Meus itens".
 *
 * Histórico (#6147):
 *
 * Cada vencimento e cada etapa da régua era um card, e reemitir o boleto criava
 * mais três: a Roberta tinha nove cards, todos pendentes (#6147). Aqui tudo o
 * que é do mesmo cliente com a mesma pessoa vira um card — D+4, D+10, retorno
 * agendado. Quando pessoas diferentes têm etapas do mesmo cliente (o contato
 * com a Dhaiane, a notificação com a Bruna), são dois cards: são duas tarefas.
 *
 * "Sem responsável" e "Débito sem contato" de quem já está em outra fila
 * somem: o cliente já tem quem cuide.
 *
 * Só junta PENDENTE e só nas filas de cobrança. Documento, concluído e
 * dispensado continuam um por linha.
 */
const SO_SE_SOZINHA: EsteiraFila[] = ['sem_responsavel', 'sem_contato'];

export function agruparPorCliente(demandas: DemandaEsteira[]): DemandaEsteira[] {
    const soltas: DemandaEsteira[] = [];
    const porCliente = new Map<string, DemandaEsteira[]>();

    for (const d of demandas) {
        const cliente = d.status === 'pendente' && ehCobrancaDoCliente(d)
            ? chaveDoCliente(d)
            : null;
        if (!cliente) { soltas.push(d); continue; }
        const lista = porCliente.get(cliente);
        if (lista) lista.push(d); else porCliente.set(cliente, [d]);
    }

    const cards: DemandaEsteira[] = [...soltas];
    for (const [cliente, doCliente] of porCliente) {
        const temOutra = doCliente.some(d => !SO_SE_SOZINHA.includes(d.fila));
        const itens = doCliente.filter(d => !(SO_SE_SOZINHA.includes(d.fila) && temOutra));
        if (itens.length) cards.push(fundir(cliente, itens));
    }
    return cards;
}

/**
 * Marca o card de cliente com retorno agendado para DEPOIS de hoje: ele sai da
 * lista e dos números até a data, e volta sozinho nela (#6147, item 2).
 *
 * Negativação não espera: pôr e tirar do SPC é obrigação com prazo, não
 * cobrança. Antes só a retirada furava a data; com o retorno obrigatório no
 * contato (#6165), "Negativar" logo depois do contato escondia o cliente de
 * quem negativa até o dia marcado. A notificação encaminhada também não espera.
 */
const NAO_ESPERA_RETORNO: EsteiraFila[] = ['negativacao_retirar', 'negativacao_pendente', 'notificacao_extrajudicial', 'negativado_spc'];

export function marcarAgendados(
    demandas: DemandaEsteira[],
    proximoPorCpf: Map<string, string>,
    agora: Date,
): DemandaEsteira[] {
    return demandas.map(d => {
        if (d.status !== 'pendente' || !ehCobrancaDoCliente(d)) return d;
        if (filasDa(d).some(f => NAO_ESPERA_RETORNO.includes(f))) return d;
        const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
        const proximo = cpf ? proximoPorCpf.get(cpf) : undefined;
        if (!proximo) return d;
        // A data vai para o card mesmo quando já chegou: o registro do contato
        // abre com ela preenchida, em vez de apagá-la sem a pessoa ver.
        if (diasDesdeISO(proximo, agora) >= 0) return { ...d, proximo_contato_em: proximo };
        return { ...d, proximo_contato_em: proximo, agendado: true };
    });
}

/**
 * Quem é o dono EFETIVO da demanda.
 *
 * Na régua, o dono é o responsável da própria etapa. Nas filas derivadas,
 * `responsavel_id` é quem GEROU o documento — o que não quer dizer que essa
 * pessoa esteja cuidando dele. Quando alguém assume pela esteira, a atribuição
 * manda; sem atribuição, a fila de documento não tem dono de verdade.
 */
export function donoEfetivo(d: DemandaEsteira): string | null {
    if (d.origem === 'cobranca_acao_manual') return d.responsavel_id;
    // Negociação e negativação: o dono é o da carteira do cliente — quem
    // assumiu ou recebeu a transferência no "Gerenciar Cobrança". Antes só
    // valia a atribuição da esteira e os 85 cards destas filas diziam
    // "ninguém assumiu" com dono na carteira (#6147).
    if (d.origem === 'cobranca_clientes') return d.responsavel_id ?? d.atribuicao?.responsavel_id ?? null;
    return d.atribuicao?.responsavel_id ?? null;
}

/**
 * Card que aparece em "Meus itens" de quem faz o contato mesmo sem ser dela
 * (pedido da Dhaiane, 07/10/2026). Duas pessoas cobram (o pool do D+4/D+10) e
 * uma cobre a outra:
 *
 *   • retorno agendado que chegou, de um cliente de alguém do pool — se a
 *     dona não puder, a outra continua o contato;
 *   • cobrança sem dono — é de onde sai cliente novo para contato, e antes
 *     só aparecia em "Todos", que ninguém abre para trabalhar.
 *
 * Quem registra o contato vira dono só do cliente que não tinha dono — o
 * retorno continua da dona (ver `registrar_contato_cobranca`).
 */
export function compartilhadaCom(d: DemandaEsteira, usuarioId: string, poolContato: string[]): boolean {
    if (d.status !== 'pendente' || d.agendado || !poolContato.includes(usuarioId)) return false;
    const filas = filasDa(d);
    const dono = donoEfetivo(d);
    if (!dono) return filas.includes('sem_responsavel');
    return dono !== usuarioId && poolContato.includes(dono) && filas.includes('followup_vencido');
}

/**
 * O card é desta pessoa: dono do card ou dona de alguma etapa dentro dele
 * (#6209 — a D+17 da Bruna no card do João aparece em "Meus itens" dela).
 */
export function ehDaPessoa(d: DemandaEsteira, usuarioId: string): boolean {
    return donoEfetivo(d) === usuarioId || (d.donos_etapas ?? []).some(o => o.id === usuarioId);
}

/**
 * Conta em "Paradas +7d" (#6211). Sem ação há mais de 7 dias — ou, mesmo com
 * contato recente, aberta há mais de 30 dias sem retorno marcado: um "tentei
 * ligar" a cada 5 dias zerava o parado e a dívida de 90 dias nunca aparecia.
 */
export const ATRASO_SEM_RETORNO_PARADO = 30;
export function estaParado(d: DemandaEsteira): boolean {
    if (d.dias_parado > 7) return true;
    return (d.dias_atraso ?? 0) > ATRASO_SEM_RETORNO_PARADO && !d.proximo_contato_em;
}

/** `true` se a demanda teve trabalho registrado na data de `agora`. */
export function trabalhadaHoje(d: DemandaEsteira, agora: Date): boolean {
    // Contato e mudança de status no "Gerenciar Cobrança" (#6211): antes não contavam
    // e o número do dia ficava muito abaixo do trabalho feito (12 contatos, 1 contado).
    if (d.trabalho_hoje?.length) return true;
    if (d.itens?.length) return d.itens.some(i => trabalhadaHoje({ ...i, itens: undefined }, agora));
    if (d.origem === 'cobranca_acao_manual') {
        const acao = d.bruto as { executado_em?: string | null } | undefined;
        return !!acao?.executado_em && diasDesdeISO(acao.executado_em, agora) === 0;
    }
    const tocado = d.atribuicao?.ultima_acao_em ?? d.atribuicao?.assumido_em;
    return !!tocado && diasDesdeISO(tocado, agora) === 0;
}

/**
 * Para quem mandar a cobrança de assinatura de um distrato.
 *
 * Os e-mails vêm de `assinantes_pendentes`, que a reconciliação copia do
 * Autentique — é o endereço para onde o convite de assinatura foi de fato, então
 * é confiável (o cadastro do cliente, não: só 9 dos 87 distratos tinham e-mail
 * no payload de geração).
 *
 * Segue a mesma regra da fila: se falta gente da Vigor, cobra-se a Vigor
 * primeiro, mesmo que o cliente também falte. Devolve `null` fora das duas filas
 * de distrato aguardando assinatura.
 */
export function destinatariosCobrancaDistrato(
    d: DemandaEsteira,
): { emails: string[]; nomes: string[]; interno: boolean } | null {
    if (d.fila !== 'distrato_aguardando_vigor' && d.fila !== 'distrato_aguardando_cliente') return null;
    const bruto = d.bruto as LogDistrato | undefined;
    const pendentes = Array.isArray(bruto?.assinantes_pendentes) ? bruto!.assinantes_pendentes! : [];
    const interno = d.fila === 'distrato_aguardando_vigor';
    const alvo = pendentes.filter(p => !!p.interno === interno && p.email);
    if (!alvo.length) return null;
    return {
        emails: [...new Set(alvo.map(p => p.email!.trim().toLowerCase()))],
        nomes: alvo.map(primeiroNome),
        interno,
    };
}

/**
 * Documentos pendentes que esperam a assinatura interna de um e-mail — os
 * mesmos que `bloqueadoresInternos` contou para aquela pessoa. É onde a
 * cobrança feita pelo card de bloqueio fica registrada.
 */
export function demandasTravadasPor(demandas: DemandaEsteira[], email: string): DemandaEsteira[] {
    const alvo = email.trim().toLowerCase();
    return demandas.filter(d => {
        if (d.status !== 'pendente') return false;
        const pendentes = (d.bruto as LogDistrato | undefined)?.assinantes_pendentes;
        return Array.isArray(pendentes)
            && pendentes.some(p => p.interno && p.email?.trim().toLowerCase() === alvo);
    });
}

/** 'YYYY-MM-DD' do dia LOCAL da data — mesma âncora de meio-dia do resto do módulo. */
export function diaLocal(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = aoMeioDia(iso);
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * A data que coloca a demanda no calendário: o PRAZO quando existe (é o que
 * tem consequência), senão o dia em que ela parou.
 *
 * ⚠️ Com os dados de hoje quase tudo cai no passado, e isso é correto, não bug:
 * o painel só guarda o que está travado. Uma notificação dentro do prazo não é
 * demanda (ver `normalizarDocumento`), então não aparece no futuro.
 */
export function dataDoCalendario(d: DemandaEsteira): string | null {
    return diaLocal(d.prazo_em) ?? diaLocal(d.referencia_em);
}

/**
 * Distribui as demandas pendentes nos dias de um mês. O que é anterior ao mês
 * vira um balde só — com 1.095 demandas paradas há mais de 7 dias, espalhá-las
 * pelos meses passados esconderia o tamanho do atraso atrás da paginação.
 *
 * `mes` é 0-based, como em `Date`.
 */
export function agruparPorDia(
    demandas: DemandaEsteira[],
    ano: number,
    mes: number,
): { dias: Map<string, DemandaEsteira[]>; antes: DemandaEsteira[]; depois: DemandaEsteira[] } {
    const prefixo = `${ano}-${String(mes + 1).padStart(2, '0')}`;
    const dias = new Map<string, DemandaEsteira[]>();
    const antes: DemandaEsteira[] = [];
    const depois: DemandaEsteira[] = [];

    for (const d of demandas) {
        if (d.status !== 'pendente' || d.agendado) continue;
        const dia = dataDoCalendario(d);
        if (!dia) continue;
        const mesDaDemanda = dia.slice(0, 7);
        if (mesDaDemanda < prefixo) antes.push(d);
        else if (mesDaDemanda > prefixo) depois.push(d);
        else {
            const lista = dias.get(dia);
            if (lista) lista.push(d); else dias.set(dia, [d]);
        }
    }
    return { dias, antes, depois };
}

/** Faixas de idade do Resumo. A última é aberta. */
export const FAIXAS_IDADE: Array<{ rotulo: string; ate: number }> = [
    { rotulo: 'até 7 dias', ate: 7 },
    { rotulo: '8 a 15 dias', ate: 15 },
    { rotulo: '16 a 30 dias', ate: 30 },
    { rotulo: '31 a 60 dias', ate: 60 },
    { rotulo: 'mais de 60 dias', ate: Infinity },
];

export interface LinhaResumoPessoa {
    /** `null` = ninguém assumiu. */
    responsavelId: string | null;
    nome: string;
    total: number;
    paradasMais7: number;
    trabalhadasHoje: number;
    /** Dias parados da demanda mais antiga da pessoa. */
    maisAntiga: number;
}

/**
 * Visão gerencial: quem está com o quê, e há quanto tempo.
 *
 * Existe para a gerente, que não trabalha fila — ela quer saber se o time dá
 * conta. Hoje montava isso exportando planilha (17 vezes em 12 dias). O dono é
 * o EFETIVO (`donoEfetivo`): nas filas de documento, quem gerou não é quem
 * cuida.
 */
export function resumoGerencial(demandas: DemandaEsteira[], agora: Date): {
    porPessoa: LinhaResumoPessoa[];
    porFaixa: Array<{ rotulo: string; total: number }>;
    totalPendente: number;
} {
    const pessoas = new Map<string, LinhaResumoPessoa>();
    const faixas = FAIXAS_IDADE.map(f => ({ rotulo: f.rotulo, total: 0 }));
    let totalPendente = 0;

    const linhaDe = (d: DemandaEsteira, dono: string | null, nomeConhecido?: string | null): LinhaResumoPessoa => {
        const chave = dono ?? '__sem_dono__';
        if (!pessoas.has(chave)) {
            const nome = nomeConhecido ? nomeConhecido : dono
                // Régua e carteira do cliente: o nome está na própria demanda.
                // Documento: em quem assumiu pela esteira.
                ? (d.origem === 'cobranca_acao_manual' ? d.responsavel_nome
                    : d.origem === 'cobranca_clientes' ? (d.responsavel_nome ?? d.atribuicao?.responsavel_nome)
                        : d.atribuicao?.responsavel_nome) || 'Sem nome'
                : 'Sem dono';
            pessoas.set(chave, { responsavelId: dono, nome, total: 0, paradasMais7: 0, trabalhadasHoje: 0, maisAntiga: 0 });
        }
        return pessoas.get(chave)!;
    };

    for (const d of demandas) {
        // Card de cliente com etapas de pessoas diferentes (#6209): cada etapa
        // conta para quem é dela — senão a D+17 da Bruna entrava no total do João.
        const partes = d.itens?.length ? d.itens : [d];
        const donos = new Map<string | null, DemandaEsteira>();
        // Etapa executada hoje conta para o dono da etapa; o contato do "Gerenciar
        // Cobrança" (trabalho_hoje) conta para QUEM FEZ (#6211) — se a Bruna liga
        // para um cliente do João, o trabalho é da Bruna. Uma vez por pessoa por card.
        const creditados = new Set<string | null>();
        for (const i of partes) {
            const dono = donoEfetivo(i);
            if (!donos.has(dono)) donos.set(dono, i);
            if (!creditados.has(dono) && trabalhadaHoje({ ...i, itens: undefined, trabalho_hoje: undefined }, agora)) {
                linhaDe(i, dono).trabalhadasHoje += 1;
                creditados.add(dono);
            }
        }
        const registros = [...(d.trabalho_hoje ?? []), ...partes.flatMap(i => (i === d ? [] : i.trabalho_hoje ?? []))];
        for (const t of registros) {
            // Registro sem autor vai para o dono do card.
            const id = t.por_id ?? donoEfetivo(d);
            if (creditados.has(id)) continue;
            linhaDe(d, id, t.por_id ? t.por : null).trabalhadasHoje += 1;
            creditados.add(id);
        }
        // Agendado não é pendência de hoje: volta para a conta na data.
        if (d.status !== 'pendente' || d.agendado || d.acompanhamento) continue;

        totalPendente += 1;
        for (const [dono, i] of donos) {
            const linha = linhaDe(i, dono);
            linha.total += 1;
            if (estaParado(d)) linha.paradasMais7 += 1;
            linha.maisAntiga = Math.max(linha.maisAntiga, d.dias_parado);
        }

        const i = FAIXAS_IDADE.findIndex(f => d.dias_parado <= f.ate);
        faixas[i === -1 ? faixas.length - 1 : i].total += 1;
    }

    // "Sem dono" vai para o fim: é um alerta, não uma pessoa para comparar.
    const porPessoa = [...pessoas.values()]
        .filter(p => p.total > 0 || p.trabalhadasHoje > 0)
        .sort((a, b) =>
            (a.responsavelId === null ? 1 : 0) - (b.responsavelId === null ? 1 : 0) || b.total - a.total
        );

    return { porPessoa, porFaixa: faixas, totalPendente };
}

/**
 * Quem da Vigor está segurando documento, e quantos.
 *
 * Nasceu do levantamento de 11/09/2026: dos 87 distratos abertos, **52
 * esperavam assinatura de gente da própria casa** — Dhaiane 26, financeiro@ 16,
 * Bruna 15, Meyryene 13. Ninguém sabia, porque a assinatura só existia no
 * Autentique. É a informação mais acionável do painel: uma mensagem para a
 * Dhaiane destrava 26 processos de uma vez.
 *
 * Lê `assinantes_pendentes`, que hoje só o distrato tem. As outras filas de
 * assinatura têm um único pendente implícito (o procurador) e não entram —
 * inventar um nome ali seria fabricar dado.
 */
export function bloqueadoresInternos(
    demandas: DemandaEsteira[],
    /**
     * E-mail corporativo → nome do perfil. Quando o e-mail é de uma pessoa do
     * sistema, o nome certo é o do perfil, não o que o Autentique guardou.
     */
    nomePorEmail?: Map<string, string>,
): Array<{ email: string; nome: string; demandas: number }> {
    const porPessoa = new Map<string, { nome: string; demandas: number }>();

    /**
     * ⚠️ O nome do Autentique não é confiável para e-mail da Vigor. Caixas
     * compartilhadas (`assinatura@`, `contato@`, `financeiro@`) aparecem lá com o
     * nome de quem o convite MENCIONAVA — em produção, "Associacao Rodoviario
     * Esportiva" (um cliente) e "Marcus Vinicius Alves" figuravam como
     * bloqueadores internos. Regra: e-mail de perfil → nome do perfil; senão é
     * caixa compartilhada e o rótulo mostra o endereço, com o nome do Autentique
     * só como complemento.
     */
    const rotulo = (email: string, nomeAutentique?: string | null): string => {
        const nome = nomeAutentique?.trim();
        // Sem mapa de perfis não há como distinguir pessoa de caixa
        // compartilhada — fica o nome do Autentique, como antes.
        if (!nomePorEmail) return nome || email.split('@')[0];
        const doPerfil = nomePorEmail.get(email);
        if (doPerfil) return doPerfil;
        const caixa = `${email.split('@')[0]}@`;
        return nome ? `${caixa} · ${nome}` : caixa;
    };

    for (const d of demandas) {
        if (d.status !== 'pendente') continue;
        const bruto = d.bruto as LogDistrato | undefined;
        const pendentes = Array.isArray(bruto?.assinantes_pendentes) ? bruto!.assinantes_pendentes! : [];
        for (const p of pendentes) {
            if (!p.interno || !p.email) continue;
            const chave = p.email.trim().toLowerCase();
            const atual = porPessoa.get(chave);
            porPessoa.set(chave, {
                nome: atual?.nome ?? rotulo(chave, p.nome),
                demandas: (atual?.demandas ?? 0) + 1,
            });
        }
    }

    return [...porPessoa.entries()]
        .map(([email, v]) => ({ email, ...v }))
        .sort((a, b) => b.demandas - a.demandas);
}

export function calcularKPIs(
    demandas: DemandaEsteira[],
    agora: Date = new Date(),
): {
    porGrupo: Record<EsteiraGrupo, number>;
    porFila: Record<EsteiraFila, number>;
    valorPendente: number;
    paradasMais7: number;
    trabalhadasHoje: number;
    semDono: number;
} {
    const porGrupo = { regua: 0, notificacoes: 0, documentos: 0, negativacao: 0, negociacao: 0 } as Record<EsteiraGrupo, number>;
    const porFila = Object.keys(GRUPO_DA_FILA).reduce((acc, f) => {
        acc[f as EsteiraFila] = 0;
        return acc;
    }, {} as Record<EsteiraFila, number>);

    let valorPendente = 0;
    let paradasMais7 = 0;
    let trabalhadasHoje = 0;
    let semDono = 0;

    for (const d of demandas) {
        // "Trabalhadas hoje" conta o que foi FEITO, inclusive o que já saiu de
        // pendente — é justamente o que se quer medir no fim do dia.
        if (trabalhadaHoje(d, agora)) trabalhadasHoje += 1;
        if (d.status !== 'pendente' || d.agendado) continue;

        porGrupo[d.grupo] += 1;
        // Card agrupado conta em cada fila que cobre — é o que o filtro de
        // fila acha quando a pessoa escolhe uma delas.
        for (const f of filasDa(d)) porFila[f] += 1;
        // Negativado só acompanha: conta no grupo e na fila, não como trabalho.
        if (d.acompanhamento) continue;
        valorPendente += Number(d.valor ?? 0);
        if (estaParado(d)) paradasMais7 += 1;
        if (!donoEfetivo(d)) semDono += 1;
    }

    return { porGrupo, porFila, valorPendente, paradasMais7, trabalhadasHoje, semDono };
}


// ─── #6211 ───────────────────────────────────────────────────────────────────

/**
 * Régua de quem já tem tratamento sai do painel (#6211): negativado está no SPC
 * (fila "Negativado no SPC"); negociado com data de pagamento futura espera a
 * data — vencida, o card volta pela regra do acordo vencido. As etapas continuam
 * no banco; só não aparecem.
 */
export function foraDaReguaPorTratamento(
    d: DemandaEsteira,
    negativados: Set<string>,
    negociadoAte: Map<string, string>,
    agora: Date,
    /**
     * Desde quando cada CPF está negativado / negociado. Com a data, só some a
     * etapa de dívida ANTERIOR ao tratamento: fatura nova vencida depois disso
     * não está no SPC nem no acordo e volta a ser cobrada. Sem data, some tudo.
     */
    desde: { negativado?: Map<string, string>; negociado?: Map<string, string> } = {},
): boolean {
    if (d.origem !== 'cobranca_acao_manual') return false;
    const cpf = (d.cpf_cnpj_cliente ?? '').replace(/\D/g, '');
    if (!cpf) return false;
    const vencimento = diaLocal((d.bruto as CobrancaAcaoManual | undefined)?.data_referencia ?? d.referencia_em);
    const anteriorA = (marco: string | undefined) => {
        const dia = diaLocal(marco);
        return !dia || !vencimento || vencimento <= dia;
    };
    if (negativados.has(cpf) && anteriorA(desde.negativado?.get(cpf))) return true;
    const ate = negociadoAte.get(cpf);
    return !!ate && diasDesdeISO(ate, agora) < 0 && anteriorA(desde.negociado?.get(cpf));
}

/**
 * "Parado" desde a última ação de verdade (#6211). O número de antes passa a ser
 * o atraso (`dias_atraso`); `dias_parado` fica o menor entre os dois. Assumir NÃO
 * conta como ação — assumir em lote apagaria o alarme.
 */
export function aplicarUltimaAcao(d: DemandaEsteira, ultimaAcaoEm: string | null | undefined, agora: Date): DemandaEsteira {
    // Negativado só acompanha: o número dele é "negativado há X dias", não "parado".
    if (d.acompanhamento) return d;
    const desdeAcao = ultimaAcaoEm ? diasDesdeISO(ultimaAcaoEm, agora) : null;
    if (desdeAcao === null || desdeAcao < 0 || desdeAcao >= d.dias_parado) return d;
    return { ...d, dias_atraso: d.dias_parado, dias_parado: desdeAcao, ultima_acao_em: ultimaAcaoEm };
}

/** A data mais recente entre as informadas (ISO), ou null. */
export function maisRecente(...datas: Array<string | null | undefined>): string | null {
    let melhor: string | null = null;
    for (const x of datas) {
        if (!x || Number.isNaN(Date.parse(x))) continue;
        if (!melhor || Date.parse(x) > Date.parse(melhor)) melhor = x;
    }
    return melhor;
}
